/**
 * New and edit for one home page banner. Structure copied from
 * vendor/isomorphic/apps/isomorphic/src/app/shared/account-settings/personal-info.tsx (the form
 * card, its FormGroup sections and `FormFooter`) and
 * vendor/isomorphic/apps/isomorphic/src/app/shared/ecommerce/category/create-category.tsx (the image
 * upload with its preview), as brands/New.tsx and brands/Detail.tsx; the sticky side card is the
 * same `WidgetCard` beside a form that gst-rates/List.tsx uses.
 *
 * Images go up the moment they are picked (`POST /api/files/images`, the one upload path every
 * catalogue image takes) and show at once from the picked file, then from the stored copy; the
 * banner itself only records their hashes. Beside the form, the live preview draws the banner as the
 * homepage will (`BannerPreview`) on every keystroke, on a desktop or a phone.
 *
 * "Auto" text colour and "Auto" overlay are worked out here, from the pixels behind the text
 * (`lib/banner-contrast.ts`), and saved as plain values — the storefront draws what the preview
 * showed and never analyses an image itself. A banner saved before styling existed opens with its
 * placement's preset, which is how the homepage already draws it. The schedule is whole days in
 * the business's time zone, the end day included (`scheduleToApi`).
 */
import { useEffect, useState, type ChangeEvent } from "react";
import { useLoaderData, useOutletContext, type LoaderFunctionArgs } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import toast from "react-hot-toast";
import {
  PiCalendarDuotone as ScheduleIcon,
  PiCursorClickDuotone as PositionIcon,
  PiDesktopDuotone as DesktopIcon,
  PiDeviceMobileDuotone as PhoneIcon,
  PiDropHalfDuotone as OverlayIcon,
  PiIdentificationBadgeDuotone as DetailsIcon,
  PiImageDuotone as ImageIcon,
  PiLinkDuotone as LinkIcon,
  PiTextAaDuotone as TypeIcon,
  PiTextTDuotone as TextIcon,
  PiToggleRightDuotone as StatusIcon,
  PiXBold as RemoveIcon,
} from "react-icons/pi";
import { ActionIcon, Button, Text } from "rizzui";
import FormFooter from "@core/components/form-footer";
import Upload from "@core/ui/upload";
import { ErrorSurface, Field, FormGroup, PageHeader, WidgetCard, problemTitle } from "@steelyard/theme";
import { BannerPreview, type PreviewMode } from "../../../components/banner-preview";
import { BrandPicker, CategoryPicker, SkuPicker } from "../../../components/catalog-pickers";
import { CheckboxCard } from "../../../components/checkbox-card";
import { DateField } from "../../../components/date-field";
import { SectionTitle } from "../../../components/section-title";
import { SelectField } from "../../../components/select-field";
import { StatusSwitch } from "../../../components/status-switch";
import { UploadBusyHint, UploadDropHint } from "../../../components/upload-drop-hint";
import { can } from "../../../lib/permissions";
import { parseIsoDate, toIsoDate } from "../../../lib/format";
import { formCard, formCardFooter, formSections } from "../../../lib/form-layout";
import { useGoBack } from "../../../lib/navigation";
import { decideReadability, formatRatio, sampleTextRegion, TARGET_CONTRAST, type Readability, type RegionLight } from "../../../lib/banner-contrast";
import { byKey } from "../../../lib/services/catalog";
import { imageUrl, useFileUpload } from "@steelyard/common";
import { bannerPath } from "../../../lib/paths";
import {
  BANNER_PRESETS,
  MOBILE_RECOMMENDED,
  PLACEMENTS,
  bannerImageFit,
  bannerImageProblem,
  createBanner,
  getBanner,
  getBannerBySlug,
  newBannerStyle,
  placementInfo,
  scheduleFromApi,
  scheduleToApi,
  updateBanner,
  type BannerLinkType,
  type BannerPlacement,
  type BannerStyle,
  type HomepageBanner,
} from "../../../lib/services/banners";
import type { AccountSummary, PermissionState } from "../../../types";

/** `/website/banners/new` has no `:slug`; an old id URL redirects to the slug URL. */
export async function bannerFormLoader({ params, request }: LoaderFunctionArgs) {
  return { banner: params.slug ? await byKey(request, params.slug, getBanner, getBannerBySlug, bannerPath) : null };
}

const isLink = (v: string) => !v || (v.startsWith("/") && !v.startsWith("//") && !v.includes("\\")) || /^https:\/\/\S+$/i.test(v);
const SIZE = z.enum(["small", "medium", "large", "x_large"]);

export const bannerSchema = z
  .object({
    name: z.string().trim().min(1, "Required").max(128),
    placement: z.string().min(1, "Choose where it appears"),
    displayOrder: z.string().trim().regex(/^\d+$/, "A whole number, 0 or more"),
    title: z.string().trim().max(128),
    subtitle: z.string().trim().max(256),
    ctaText: z.string().trim().max(40),
    showButton: z.boolean(),
    altText: z.string().trim().max(256),
    /** "light" and "dark" name the TEXT; the API's `textTone` names the ground under it. */
    textColor: z.enum(["auto", "light", "dark"]),
    titleSize: SIZE,
    subtitleSize: SIZE,
    titleWeight: z.enum(["medium", "semibold", "bold"]),
    align: z.enum(["left", "center", "right"]),
    verticalAlign: z.enum(["top", "center", "bottom"]),
    padding: z.enum(["compact", "normal", "spacious"]),
    imageFocus: z.enum(["left", "center", "right"]),
    overlay: z.enum(["auto", "none", "gradient", "solid"]),
    overlayOpacity: z.enum(["10", "20", "30", "40", "50", "60", "70", "80"]),
    ctaUrl: z.string().trim().max(512).refine(isLink, "A storefront path starting with / (e.g. /catalogue) or an https:// address"),
    linkType: z.enum(["", "category", "brand", "sku"]),
    linkTargetId: z.string(),
    start: z.string(),
    end: z.string(),
    isActive: z.boolean(),
  })
  .superRefine((v, ctx) => {
    if (v.linkType && !v.linkTargetId) ctx.addIssue({ path: ["linkTargetId"], code: "custom", message: "Choose the record it links to" });
    if (!v.ctaUrl && !v.linkType) ctx.addIssue({ path: ["ctaUrl"], code: "custom", message: "Give a link URL, or link it to a category, brand or product" });
    if (v.start && v.end && v.end < v.start) ctx.addIssue({ path: ["end"], code: "custom", message: "The last day is before the first" });
  });
export type BannerFormValues = z.infer<typeof bannerSchema>;

type StyleFields = Pick<
  BannerFormValues,
  "textColor" | "titleSize" | "subtitleSize" | "titleWeight" | "align" | "verticalAlign" | "padding" | "imageFocus" | "overlay" | "overlayOpacity" | "showButton"
>;

/** The form's style fields for a style — `tone` is the API's `textTone`, the ground under the text. */
export function styleFields(style: BannerStyle, tone: "dark" | "light"): StyleFields {
  return {
    textColor: style.autoTextColor ? "auto" : tone === "dark" ? "light" : "dark",
    titleSize: style.titleSize,
    subtitleSize: style.subtitleSize,
    titleWeight: style.titleWeight,
    align: style.align,
    verticalAlign: style.verticalAlign,
    padding: style.padding,
    imageFocus: style.imageFocus,
    overlay: style.autoOverlay ? "auto" : style.overlay,
    // A step to start from if someone switches an overlay on; "none" saves 0 regardless.
    overlayOpacity: String(style.overlayOpacity > 0 ? style.overlayOpacity : 40) as StyleFields["overlayOpacity"],
    showButton: style.showButton,
  };
}

function defaultsOf(banner: HomepageBanner | null): BannerFormValues {
  const { start, end } = banner ? scheduleFromApi(banner) : { start: "", end: "" };
  // A new banner has no placement yet: hero centre's defaults stand in until one is chosen.
  const style = banner ? (banner.style ?? BANNER_PRESETS[banner.placement]) : newBannerStyle("hero_center");
  return {
    name: banner?.name ?? "",
    placement: banner?.placement ?? "",
    displayOrder: String(banner?.displayOrder ?? 1),
    title: banner?.title ?? "",
    subtitle: banner?.subtitle ?? "",
    ctaText: banner?.ctaText ?? "",
    altText: banner?.altText ?? "",
    ...styleFields(style, banner?.textTone ?? "dark"),
    ctaUrl: banner?.ctaUrl ?? (banner ? "" : "/catalogue"),
    linkType: banner?.linkType ?? "",
    linkTargetId: banner?.linkTargetId ?? "",
    start,
    end,
    isActive: banner?.isActive ?? true,
  };
}

/**
 * What the banner will be drawn with: the chosen colour and overlay, with "Auto" ones decided from
 * the image (`light`, when it could be read). Without a reading an "Auto" colour keeps the banner's
 * current one and an "Auto" overlay draws none.
 */
export function resolvePresentation(
  v: Pick<BannerFormValues, "textColor" | "overlay" | "overlayOpacity">,
  light: RegionLight | null,
  savedTone: "dark" | "light",
): { tone: "dark" | "light"; overlay: BannerStyle["overlay"]; overlayOpacity: number; readability: Readability | null } {
  const manualTone = v.textColor === "light" ? "dark" : v.textColor === "dark" ? "light" : savedTone;
  const choice = {
    tone: manualTone,
    autoTone: v.textColor === "auto",
    overlay: v.overlay === "auto" ? ("none" as const) : v.overlay,
    overlayOpacity: Number(v.overlayOpacity),
    autoOverlay: v.overlay === "auto",
  };
  if (!light) return { tone: manualTone, overlay: choice.overlay, overlayOpacity: choice.overlay === "none" ? 0 : choice.overlayOpacity, readability: null };
  const r = decideReadability(light, choice);
  return { tone: r.tone, overlay: r.overlay, overlayOpacity: r.overlay === "none" ? 0 : r.overlayOpacity, readability: r };
}

/** One image slot: pick a file, see it at once (then the stored copy), remove it. */
function ImageSlot({
  label,
  hash,
  required,
  recommended,
  followsImage,
  hint,
  onChange,
}: {
  label: string;
  hash: string | null;
  required?: boolean;
  recommended: { w: number; h: number } | undefined;
  followsImage: boolean;
  hint?: string;
  /** The stored hash once uploaded; `src` is what to show meanwhile (the picked file itself). */
  onChange: (image: { hash: string | null; src: string | null }) => void;
}) {
  // The pre-check's own refusal; the upload's error is `upload.error`.
  const [error, setError] = useState<unknown>(null);
  const [local, setLocal] = useState<string | null>(null);
  const [fit, setFit] = useState<string | null>(null);
  const upload = useFileUpload("image", {
    onUploaded: (result) => {
      setLocal(null);
      onChange({ hash: result.hash, src: imageUrl(result.hash) });
      toast.success(`${label} uploaded`);
    },
    onError: () => {
      setLocal(null);
      onChange({ hash, src: hash ? imageUrl(hash) : null });
      toast.error(`Could not upload the ${label.toLowerCase()}`);
    },
  });
  const busy = upload.busy;

  useEffect(() => () => void (local && URL.revokeObjectURL(local)), [local]);

  function pick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    if (!file) return;
    const problem = bannerImageProblem(file);
    if (problem) {
      event.target.value = ""; // re-picking the same file must still fire a change
      setError({ title: "Not a supported image", detail: problem, status: 400 });
      return;
    }
    setError(null);
    const preview = URL.createObjectURL(file);
    setLocal(preview);
    onChange({ hash, src: preview });
    void upload.onChange(event);
  }

  const shown = local ?? (hash ? imageUrl(hash) : null);
  return (
    <div className="flex flex-col gap-2">
      <Text className="font-medium text-gray-900">
        {label}
        {required ? <span aria-hidden="true" className="text-red"> *</span> : null}
      </Text>
      {recommended ? (
        <Text className="text-xs text-gray-500">
          Recommended {recommended.w} × {recommended.h}px{followsImage ? " — the slot takes the image's own shape" : ""}. JPEG, PNG, WebP or GIF, up to 5 MB.
        </Text>
      ) : null}
      {hint ? <Text className="text-xs text-gray-500">{hint}</Text> : null}
      {shown ? (
        <div className="relative w-fit">
          <img
            src={shown}
            alt={`${label} preview`}
            className="max-h-40 rounded-lg border border-muted object-contain"
            onLoad={(e) =>
              setFit(recommended ? bannerImageFit({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight }, recommended, followsImage) : null)
            }
          />
          {!busy ? (
            <ActionIcon
              type="button"
              size="sm"
              variant="solid"
              color="danger"
              className="absolute -right-2 -top-2"
              aria-label={`Remove the ${label.toLowerCase()}`}
              onClick={() => {
                setFit(null);
                onChange({ hash: null, src: null });
              }}
            >
              <RemoveIcon aria-hidden className="size-3.5" />
            </ActionIcon>
          ) : null}
        </div>
      ) : null}
      {shown && fit ? <Text className="text-xs text-orange-dark">{fit}</Text> : null}
      <Upload
        accept="img"
        multiple={false}
        disabled={busy}
        onChange={pick}
        aria-label={`${label} file`}
        placeholderText={busy ? <UploadBusyHint label="Uploading…" /> : <UploadDropHint />}
        iconClassName="size-8 shrink-0"
        className="p-4"
        wrapperClassName="w-full"
      />
      {error ?? upload.error ? <ErrorSurface error={error ?? upload.error} action={`upload the ${label.toLowerCase()}`} /> : null}
    </div>
  );
}

/** How readable the text is, in words a person can act on. */
function contrastNote(readability: Readability | null, hasImage: boolean): { text: string; ok: boolean } | null {
  if (!hasImage) return null;
  if (!readability) return { text: "Contrast could not be checked for this image.", ok: true };
  const ratio = formatRatio(readability.ratio);
  if (readability.ratio >= TARGET_CONTRAST) return { text: `Text contrast ${ratio} — easy to read.`, ok: true };
  if (readability.ratio >= 3) return { text: `Text contrast ${ratio} — fine for the title, weak for small text. An overlay would help.`, ok: false };
  return { text: `Text contrast ${ratio} — hard to read. Add an overlay, switch the text colour, or move the text.`, ok: false };
}

export default function BannerForm() {
  const { banner } = useLoaderData() as Awaited<ReturnType<typeof bannerFormLoader>>;
  const { permissionState } = useOutletContext<{ account: AccountSummary; permissionState: PermissionState }>();
  const goBack = useGoBack("/website/banners");
  const [desktop, setDesktop] = useState({ hash: banner?.desktopImageHash ?? null, src: banner ? imageUrl(banner.desktopImageHash) : null });
  const [mobile, setMobile] = useState({
    hash: banner?.mobileImageHash ?? null,
    src: banner?.mobileImageHash ? imageUrl(banner.mobileImageHash) : null,
  });
  const [imageMissing, setImageMissing] = useState(false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [mode, setMode] = useState<PreviewMode>("desktop");
  const [light, setLight] = useState<RegionLight | null>(null);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<BannerFormValues>({ resolver: zodResolver(bannerSchema), defaultValues: defaultsOf(banner) });

  const v = watch();
  const info = placementInfo(v.placement) ?? placementInfo("hero_center")!;
  const regionKey = `${desktop.src}|${v.align}|${v.verticalAlign}|${v.padding}|${v.imageFocus}|${info.value}`;

  // Re-read the pixels behind the text whenever the image, where the text sits, or the crop changes.
  useEffect(() => {
    let current = true;
    if (!desktop.src) {
      setLight(null);
      return;
    }
    void sampleTextRegion(desktop.src, info.recommended.w / info.recommended.h, v).then((reading) => current && setLight(reading));
    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- regionKey names exactly the inputs that move the region
  }, [regionKey]);

  if (!can(permissionState, "catalogue.administer")) {
    return <ErrorSurface error={{ title: "Not permitted", status: 403 }} action="manage the home page banners" />;
  }

  const drawn = resolvePresentation(v, light, banner?.textTone ?? "dark");
  const note = contrastNote(drawn.readability, Boolean(desktop.src) && info.showsText);
  const previewStyle: BannerStyle = {
    titleSize: v.titleSize,
    subtitleSize: v.subtitleSize,
    titleWeight: v.titleWeight,
    align: v.align,
    verticalAlign: v.verticalAlign,
    padding: v.padding,
    imageFocus: v.imageFocus,
    overlay: drawn.overlay,
    overlayOpacity: drawn.overlayOpacity,
    showButton: v.showButton,
    autoTextColor: v.textColor === "auto",
    autoOverlay: v.overlay === "auto",
  };
  const showsOnPhones = Boolean(info.phone);

  /** A placement's own text defaults — for a new banner when its placement is chosen, or on request. */
  function applyPlacementDefaults(placement: BannerPlacement) {
    const fields = styleFields(banner ? BANNER_PRESETS[placement] : newBannerStyle(placement), banner?.textTone ?? "dark");
    for (const [key, value] of Object.entries(fields)) setValue(key as keyof StyleFields, value as never, { shouldDirty: true });
  }

  const submit = handleSubmit(async (values) => {
    setSubmitError(null);
    if (!desktop.hash) {
      setImageMissing(true);
      return;
    }
    const input = {
      name: values.name,
      placement: values.placement as BannerPlacement,
      desktopImageHash: desktop.hash,
      mobileImageHash: mobile.hash,
      title: values.title || null,
      subtitle: values.subtitle || null,
      ctaText: values.ctaText || null,
      ctaUrl: values.ctaUrl || null,
      linkType: (values.linkType || null) as BannerLinkType | null,
      linkTargetId: values.linkType ? values.linkTargetId : null,
      altText: values.altText || null,
      textTone: drawn.tone,
      displayOrder: Number(values.displayOrder),
      ...scheduleToApi(values.start, values.end),
      isActive: values.isActive,
      style: { ...previewStyle, overlay: drawn.overlay, overlayOpacity: drawn.overlayOpacity },
    };
    try {
      if (banner) await updateBanner(banner.id, input);
      else await createBanner(input);
      toast.success(banner ? "Banner saved" : "Banner created");
      goBack();
    } catch (err) {
      setSubmitError(err);
      toast.error(problemTitle(err, banner ? "Could not save the banner" : "Could not create the banner"));
    }
  });

  const linkType = v.linkType;
  const pickTarget = (id: string) => setValue("linkTargetId", id, { shouldValidate: true });
  const overlayManual = v.overlay === "gradient" || v.overlay === "solid";

  return (
    <div className="flex flex-col gap-6 @container">
      <PageHeader
        title={banner ? `Edit ${banner.name}` : "New Banner"}
        breadcrumb={[{ name: "Home", href: "/" }, { name: "Website · Homepage Banners", href: "/website/banners" }, { name: banner ? banner.name : "New Banner" }]}
      />

      <div className="grid grid-cols-1 items-start gap-6 xl:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
        <form noValidate onSubmit={submit} className={formCard}>
          <div className={formSections}>
            {submitError ? <ErrorSurface error={submitError} action={banner ? "save this banner" : "create this banner"} /> : null}

            <FormGroup title={<SectionTitle icon={DetailsIcon}>Banner</SectionTitle>} description="Where it appears on the home page. The name is for this list only; customers never see it.">
              <Field label="Name" required maxLength={128} autoComplete="off" error={errors.name?.message} {...register("name")} />
              <SelectField
                label="Placement"
                required
                error={errors.placement?.message}
                hint={v.placement ? `${info.size}. Shown on ${info.phone ? "every screen" : info.shownFrom}.` : undefined}
                {...register("placement", { onChange: (event) => !banner && event.target.value && applyPlacementDefaults(event.target.value) })}
              >
                <option value="">Choose a slot…</option>
                {PLACEMENTS.map((p) => (
                  <option key={p.value} value={p.value}>
                    {p.label}
                  </option>
                ))}
              </SelectField>
              <Field
                label="Display order"
                required
                inputMode="numeric"
                hint="Lowest first within a placement. A one-banner slot shows the first live one."
                error={errors.displayOrder?.message}
                {...register("displayOrder")}
              />
            </FormGroup>

            <FormGroup title={<SectionTitle icon={ImageIcon}>Image</SectionTitle>} description="The preview beside this form shows how the slot crops it.">
              <div className="col-span-full grid grid-cols-1 gap-6 @3xl:grid-cols-2">
                <ImageSlot
                  label="Desktop image"
                  required
                  hash={desktop.hash}
                  recommended={info.recommended}
                  followsImage={info.shape === "image"}
                  onChange={(image) => {
                    setDesktop(image);
                    if (image.hash) setImageMissing(false);
                  }}
                />
                {showsOnPhones || mobile.hash ? (
                  <ImageSlot
                    label="Mobile image"
                    hash={mobile.hash}
                    recommended={MOBILE_RECOMMENDED[info.value]}
                    followsImage={info.phone?.shape !== 3}
                    hint={
                      showsOnPhones
                        ? "Optional. Shown below 768px wide; without one, phones get the desktop image."
                        : "This placement is not shown on phones, so a mobile image is never used."
                    }
                    onChange={(image) => {
                      setMobile(image);
                      if (image.src) setMode("phone");
                    }}
                  />
                ) : null}
              </div>
              {imageMissing ? (
                <Text role="alert" className="col-span-full text-sm text-red">
                  Upload a desktop image — every banner needs one.
                </Text>
              ) : null}
              <SelectField label="Image focus" hint="The part of the image kept when the slot crops it." {...register("imageFocus")}>
                <option value="left">Left</option>
                <option value="center">Centre</option>
                <option value="right">Right</option>
              </SelectField>
            </FormGroup>

            <FormGroup title={<SectionTitle icon={TextIcon}>Content</SectionTitle>} description={info.showsText ? "Drawn over the image, so it stays sharp at any size. All optional." : "This placement draws its own category list over the image, so it shows no banner text."}>
              {info.showsText ? (
                <>
                  <Field label="Title" maxLength={128} autoComplete="off" error={errors.title?.message} {...register("title")} />
                  <Field label="Subtitle" maxLength={256} autoComplete="off" error={errors.subtitle?.message} {...register("subtitle")} />
                  <Field label="Button text" maxLength={40} autoComplete="off" placeholder="View range" error={errors.ctaText?.message} {...register("ctaText")} />
                  <CheckboxCard label="Show the button" helperText="The whole banner is the link either way." {...register("showButton")} />
                </>
              ) : null}
              <Field
                label="Alt text"
                maxLength={256}
                autoComplete="off"
                wrapperClassName="col-span-full"
                hint="What the image shows, for screen readers. Defaults to the title."
                error={errors.altText?.message}
                {...register("altText")}
              />
            </FormGroup>

            {info.showsText ? (
              <>
                <FormGroup
                  title={<SectionTitle icon={TypeIcon}>Typography</SectionTitle>}
                  description="In the homepage's own face. Sizes scale with the banner, so they hold on a phone."
                >
                  <SelectField label="Text colour" hint="Auto picks whichever reads better on this image." {...register("textColor")}>
                    <option value="auto">Auto</option>
                    <option value="light">Light text — for dark images</option>
                    <option value="dark">Dark text — for light images</option>
                  </SelectField>
                  <SelectField label="Title size" {...register("titleSize")}>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                    <option value="x_large">Extra large</option>
                  </SelectField>
                  <SelectField label="Subtitle size" {...register("subtitleSize")}>
                    <option value="small">Small</option>
                    <option value="medium">Medium</option>
                    <option value="large">Large</option>
                  </SelectField>
                  <SelectField label="Title weight" {...register("titleWeight")}>
                    <option value="medium">Medium</option>
                    <option value="semibold">Semibold</option>
                    <option value="bold">Bold</option>
                  </SelectField>
                  <div className="col-span-full">
                    <Button type="button" variant="text" size="sm" className="px-0" onClick={() => applyPlacementDefaults(info.value)}>
                      Reset typography, position and overlay to this placement&apos;s defaults
                    </Button>
                  </div>
                </FormGroup>

                <FormGroup title={<SectionTitle icon={PositionIcon}>Position</SectionTitle>} description="Where the text sits over the image; it aligns the same way.">
                  <SelectField label="Horizontal" {...register("align")}>
                    <option value="left">Left</option>
                    <option value="center">Centre</option>
                    <option value="right">Right</option>
                  </SelectField>
                  <SelectField label="Vertical" {...register("verticalAlign")}>
                    <option value="top">Top</option>
                    <option value="center">Middle</option>
                    <option value="bottom">Bottom</option>
                  </SelectField>
                  <SelectField label="Padding" hint="How far the text keeps from the banner's edges." {...register("padding")}>
                    <option value="compact">Compact</option>
                    <option value="normal">Normal</option>
                    <option value="spacious">Spacious</option>
                  </SelectField>
                </FormGroup>
              </>
            ) : null}

            <FormGroup title={<SectionTitle icon={OverlayIcon}>Overlay</SectionTitle>} description="Shading between the image and the text, in the colour that contrasts with the text.">
              <SelectField label="Overlay" hint="Auto adds the lightest shading that makes the text readable, and none if it already is." {...register("overlay")}>
                <option value="auto">Auto</option>
                <option value="none">None</option>
                <option value="gradient">Gradient — strongest behind the text</option>
                <option value="solid">Solid — an even tint</option>
              </SelectField>
              <SelectField
                label="Opacity"
                disabled={!overlayManual}
                disabledReason={v.overlay === "auto" ? `Auto: ${drawn.overlay === "none" ? "no overlay needed" : `${drawn.overlayOpacity}% gradient`}` : "No overlay"}
                {...register("overlayOpacity")}
              >
                {["10", "20", "30", "40", "50", "60", "70", "80"].map((step) => (
                  <option key={step} value={step}>
                    {step}%
                  </option>
                ))}
              </SelectField>
            </FormGroup>

            <FormGroup title={<SectionTitle icon={LinkIcon}>Link</SectionTitle>} description="Where a click goes: a URL, or a catalogue category, brand or product. A URL wins when both are set.">
              <Field
                label="Link URL"
                autoComplete="off"
                placeholder="/catalogue"
                hint="A storefront path such as /catalogue?q=angle, or an https:// address."
                error={errors.ctaUrl?.message}
                {...register("ctaUrl")}
              />
              <SelectField label="Catalogue link" {...register("linkType", { onChange: () => setValue("linkTargetId", "") })}>
                <option value="" data-choice>
                  None
                </option>
                <option value="category">Category</option>
                <option value="brand">Brand</option>
                <option value="sku">Product (SKU)</option>
              </SelectField>
              {linkType === "category" ? <CategoryPicker required value={v.linkTargetId} onChange={pickTarget} error={errors.linkTargetId?.message} /> : null}
              {linkType === "brand" ? <BrandPicker required value={v.linkTargetId} onChange={pickTarget} error={errors.linkTargetId?.message} /> : null}
              {linkType === "sku" ? <SkuPicker required value={v.linkTargetId} onChange={pickTarget} error={errors.linkTargetId?.message} /> : null}
            </FormGroup>

            <FormGroup title={<SectionTitle icon={ScheduleIcon}>Schedule</SectionTitle>} description="Whole days in the business's time zone, both included. Leave either empty for no limit.">
              <DateField
                dateFormat="dd-MMM-yyyy"
                placeholderText="From now"
                selected={parseIsoDate(v.start || null)}
                maxDate={parseIsoDate(v.end || null) ?? undefined}
                onChange={(date) => setValue("start", date ? toIsoDate(date as Date) : "", { shouldValidate: true })}
                inputProps={{ label: "Start date", clearable: Boolean(v.start), onClear: () => setValue("start", "", { shouldValidate: true }) }}
              />
              <DateField
                dateFormat="dd-MMM-yyyy"
                placeholderText="No end date"
                selected={parseIsoDate(v.end || null)}
                minDate={parseIsoDate(v.start || null) ?? undefined}
                onChange={(date) => setValue("end", date ? toIsoDate(date as Date) : "", { shouldValidate: true })}
                inputProps={{
                  label: "End date",
                  error: errors.end?.message,
                  clearable: Boolean(v.end),
                  onClear: () => setValue("end", "", { shouldValidate: true }),
                }}
              />
            </FormGroup>

            <FormGroup title={<SectionTitle icon={StatusIcon}>Status</SectionTitle>} description="Whether the banner is shown at all.">
              <StatusSwitch activeText="Shown on the home page while inside its dates." inactiveText="Not shown, whatever its dates." {...register("isActive")} />
            </FormGroup>
          </div>

          <FormFooter
            className={formCardFooter}
            submitBtnText={isSubmitting ? "Saving…" : banner ? "Save banner" : "Create banner"}
            isLoading={isSubmitting}
            altBtnText="Cancel"
            handleAltBtn={goBack}
          />
        </form>

        <WidgetCard
          title="Preview"
          description={v.placement ? `${info.label}, about as the homepage draws it.` : "Choose a placement to preview it in its slot."}
          headerClassName="border-muted border-b"
          className="xl:sticky xl:top-24"
          action={
            <div className="flex gap-1" role="group" aria-label="Preview size">
              <Button size="sm" variant={mode === "desktop" ? "solid" : "outline"} aria-pressed={mode === "desktop"} onClick={() => setMode("desktop")}>
                <DesktopIcon aria-hidden className="me-1 size-4" />
                Desktop
              </Button>
              <Button size="sm" variant={mode === "phone" ? "solid" : "outline"} aria-pressed={mode === "phone"} onClick={() => setMode("phone")}>
                <PhoneIcon aria-hidden className="me-1 size-4" />
                Phone
              </Button>
            </div>
          }
        >
          <div className="flex flex-col gap-3 p-4">
            <BannerPreview
              placement={info}
              mode={mode}
              desktopSrc={desktop.src}
              mobileSrc={mobile.src}
              title={v.title}
              subtitle={v.subtitle}
              ctaText={v.ctaText}
              tone={drawn.tone}
              style={previewStyle}
            />
            {note ? (
              <Text role="status" className={note.ok ? "text-sm text-green-dark" : "text-sm text-orange-dark"}>
                {note.text}
              </Text>
            ) : null}
          </div>
        </WidgetCard>
      </div>
    </div>
  );
}
